import { freeze } from '@reduxjs/toolkit';
import {
  AnalysisStatus,
  Geo,
  IAnalysisInfoFieldsFragment,
  ICreatorFieldsFragment,
  IComparisonConfig,
  QueryType,
  TimeUnit,
  IDatePickerFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import {
  IChartSettings,
  IParsedChartData,
} from '../../../components/Chart/interfaces';
import { IBreakdownOverallStatus, MaybeNil } from '../../../utils/interfaces';
import { IChartView } from '../../../components/Chart/types';
import { IAnalysisResponse } from '../../../utils/apollo/responseTypes';
import { IIncident } from '../../../pages/Incidents/interfaces';

// TODO: move to redux-modals later
export interface IModalsState {
  isCreateIncidentModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isScheduleCreateModalOpen: boolean;
  isScheduleDeleteModalOpen: boolean;
  createIncidentData: any;
}

export const defaultModalsState = freeze<IModalsState>(
  {
    isDeleteModalOpen: false,
    isCreateIncidentModalOpen: false,
    isScheduleCreateModalOpen: false,
    isScheduleDeleteModalOpen: false,
    createIncidentData: null,
  },
  true,
);

export interface IAnalysisComparisonInfo {
  isEmpty: boolean;
  comparisonConfig: IComparisonConfig;
}

export interface IAnalysisSummaryRichText {
  richText: { html: MaybeNil<string> };
  showInDashboard: boolean;
}

// metadata is the immutable information of the analysis
export interface IAnalysisMetadata {
  analysisId: string;
  display: string;
  description: string;
  startDate: string;
  endDate: string;
  queryType: QueryType;
  status: AnalysisStatus;
  timeUnit: TimeUnit;
  isFavorite: boolean;
  exportUrl: string | null;
  referenceSql: string | null;
  executedAt: string;
  isBreakdownOverall: IBreakdownOverallStatus;
  geo: Geo | null;
  creator: ICreatorFieldsFragment;
  container: IAnalysisResponse['container'];
  query: IAnalysisResponse['query'];
  datePicker: IDatePickerFieldsFragment;
  // In compare mode we need to know if data exists and config itself
  comparisonInfo: IAnalysisComparisonInfo | null;
  richTextSummary: IAnalysisSummaryRichText | null;
  reportLink: string;
  chartView: IChartView;
}

/**
 * Settings contains all mutable information of the analysis
 * which will be changed upon user actions.
 */
export interface IAnalysisSettings {
  chartSettings: IChartSettings;
  chartSettingsSnapshot: IChartSettings;
  // should be moved to outside of analysis state
  incidents: Record<string, IIncident[]>;
  chartWidth: number;
  inspectSubjectId: string | null;
}
export interface IAnalysisState {
  // Future Improvement, only store the builderInfo at runtime.
  // Otherwise we always store two set of data on query and basic info
  //
  // builderInfo is a set of analysis fields that we primarily use
  // for BuilderInfo component. It includes some common fields from Analysis
  // and query that is different for each QueryType
  builderInfo: IAnalysisInfoFieldsFragment | null;
  modalsState: IModalsState;
  // Settings contains all mutable information of the analysis
  settings: IAnalysisSettings;
  // only available when fetch full analysis with SUCCESS Status in GET_ANALYSIS
  chartData: IParsedChartData | null;
  // Using metadata to store the information of the analysis which won't be changed at runtime.
  // Metadata always be accessible no matter the analysis is FAILED or SUCCESS
  metadata: IAnalysisMetadata | null;
  // defaultChartSettings is the settings for the analysis when it is created.
  // this can help us to restore the settings as needed.
  defaultChartSettings: IChartSettings;
}

export type IAnalysesState = Record<string, IAnalysisState>;
